import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Button, 
  Breadcrumbs,
  Link as MuiLink,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  Rating,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Snackbar,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Store as StoreIcon,
  LocalShipping as ShippingIcon,
  AccessTime as TimeIcon,
  Verified as VerifiedIcon,
  AccessTime,
  Home as HomeIcon,
  LocalPharmacy as PharmacyIcon,
  MedicalServices as MedicalIcon,
  Healing as HealingIcon
} from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../contexts/CartContext';
import medicineProductBn from '../i18n/bn/medicineProduct';
import medicineProductEn from '../i18n/en/medicineProduct';
import medicineProductBt from '../i18n/bt/medicineProduct';
import medicineProductDe from '../i18n/de/medicineProduct';
import medicineProductSl from '../i18n/sl/medicineProduct';
import medicineProductAf from '../i18n/af/medicineProduct';
import medicineProductCn from '../i18n/cn/medicineProduct';
// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const MedicineBazarProductDetail = () => {
  const currentLanguage = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
  const t = currentLanguage === 'en' ? medicineProductEn : currentLanguage === 'bt' ? medicineProductBt : currentLanguage === 'de' ? medicineProductDe : currentLanguage === 'sl' ? medicineProductSl : currentLanguage === 'af' ? medicineProductAf : currentLanguage === 'cn' ? medicineProductCn : medicineProductBn;
  const { id } = useParams();
  const { addToCart } = useCart();
  const [tabValue, setTabValue] = useState(0);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedUnit, setSelectedUnit] = useState('strip');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const unitConversions = {
    strip: { tablets: 10, label: t.labels.unitStrip, price: 1 },
    box: { tablets: 100, label: t.labels.unitBox, price: 10 },
    tablet: { tablets: 1, label: t.labels.unitTablet, price: 0.1 },
    bottle: { ml: 100, label: t.labels.unitBottle, price: 1 },
    vial: { ml: 5, label: t.labels.unitVial, price: 0.5 },
    ampule: { ml: 2, label: t.labels.unitAmpule, price: 0.3 },
    tube: { gm: 10, label: t.labels.unitTube, price: 1 },
    sachet: { gm: 1, label: t.labels.unitSachet, price: 0.2 },
    inhaler: { doses: 200, label: t.labels.unitInhaler, price: 1 }
  };

  const handleUnitChange = (event) => {
    const newUnit = event.target.value;
    setSelectedUnit(newUnit);
    
    // Adjust quantity based on unit conversion
    const oldUnitTotal = quantity * (unitConversions[selectedUnit].tablets || 
                                   unitConversions[selectedUnit].ml || 
                                   unitConversions[selectedUnit].gm || 
                                   unitConversions[selectedUnit].doses);
    
    const newUnitSize = (unitConversions[newUnit].tablets || 
                        unitConversions[newUnit].ml || 
                        unitConversions[newUnit].gm || 
                        unitConversions[newUnit].doses);
    
    const newQuantity = Math.max(1, Math.round(oldUnitTotal / newUnitSize));
    setQuantity(newQuantity);
  };

  const getUnitInfo = (unit) => {
    const info = unitConversions[unit];
    if (info.tablets) return `${info.tablets} ${currentLanguage === 'en' ? 'tablets' : 'ট্যাবলেট'}`;
    if (info.ml) return `${info.ml} ${currentLanguage === 'en' ? 'ml' : 'মিলি'}`;
    if (info.gm) return `${info.gm} ${currentLanguage === 'en' ? 'gm' : 'গ্রাম'}`;
    if (info.doses) return `${info.doses} ${currentLanguage === 'en' ? 'doses' : 'ডোজ'}`;
    return '';
  };

  const calculateTotalUnits = () => {
    const unitInfo = unitConversions[selectedUnit];
    const totalBase = quantity * (unitInfo.tablets || unitInfo.ml || unitInfo.gm || unitInfo.doses);
    
    if (unitInfo.tablets) return `${currentLanguage === 'en' ? 'Total' : 'মোট'} ${totalBase} ${currentLanguage === 'en' ? 'tablets' : 'ট্যাবলেট'}`;
    if (unitInfo.ml) return `${currentLanguage === 'en' ? 'Total' : 'মোট'} ${totalBase} ${currentLanguage === 'en' ? 'ml' : 'মিলি'}`;
    if (unitInfo.gm) return `${currentLanguage === 'en' ? 'Total' : 'মোট'} ${totalBase} ${currentLanguage === 'en' ? 'gm' : 'গ্রাম'}`;
    if (unitInfo.doses) return `${currentLanguage === 'en' ? 'Total' : 'মোট'} ${totalBase} ${currentLanguage === 'en' ? 'doses' : 'ডোজ'}`;
    return '';
  };

  // Mock data for medicine product details
  useEffect(() => {
    // In a real app, this would be an API call
    setTimeout(() => {
      setProduct({
        id: parseInt(id),
        name: `${id === '1' ? 'নাপা এক্সট্রা ট্যাবলেট' : id === '2' ? 'সেক্লো ক্যাপসুল' : 'অ্যালার্জিন সিরাপ'}`,
        description: 'এটি একটি উচ্চ মানের ঔষধ যা বিশ্বস্ত ফার্মাসিউটিক্যাল কোম্পানি দ্বারা উৎপাদিত। প্রতিটি ঔষধ নিয়মিত মান পরীক্ষা এবং কঠোর মান নিয়ন্ত্রণের মাধ্যমে প্রস্তুত করা হয়।',
        price: 120,
        salePrice: 100,
        unit: id === '1' ? 'strip' : id === '2' ? 'box' : 'bottle',
        rating: 4.9,
        reviewCount: 245,
        stock: 50,
        images: [
          `https://source.unsplash.com/random/600x400?medicine&sig=1`,
          `https://source.unsplash.com/random/600x400?medicine&sig=2`,
          `https://source.unsplash.com/random/600x400?medicine&sig=3`,
        ],
        store: {
          id: 1,
          name: 'লাজ ফার্মেসি',
          rating: 4.8,
          isVerified: true
        },
        bazar: {
          id: 1,
          name: 'চাঁচকৈর বাজার'
        },
        category: 'ঔষধ',
        manufacturer: 'স্কয়ার ফার্মাসিউটিক্যালস',
        expiryDate: '২০২৫-১২-৩১',
        deliveryTime: '৩০-৬০ মিনিট',
        minOrder: 1,
        prescriptionRequired: true,
        attributes: [
          { name: 'জেনেরিক নাম', value: 'প্যারাসিটামল' },
          { name: 'শক্তি', value: '৫০০ মিলিগ্রাম' },
          { name: 'প্রস্তুতকারক', value: 'স্কয়ার ফার্মাসিউটিক্যালস' },
          { name: 'সংরক্ষণ', value: 'শীতল ও শুষ্ক স্থানে রাখুন' },
          { name: 'মেয়াদ শেষ', value: '৩১ ডিসেম্বর ২০২৫' },
          { name: 'ব্যাচ নং', value: 'BN2024001' }
        ]
      });
      
      setLoading(false);
    }, 500);
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value) && value > 0 && value <= product.stock) {
      setQuantity(value);
    }
  };

  const increaseQuantity = () => {
    if (quantity < product.stock) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleAddToCart = () => {
    addToCart(
      { 
        id: product.id, 
        name: product.name, 
        price: product.salePrice || product.price,
        image: product.images[0],
        store: product.store.name,
        stock: product.stock
      }, 
      quantity
    );
    
    setSnackbar({
      open: true,
      message: `${product.name} কার্টে যোগ করা হয়েছে!`,
      severity: 'success'
    });
    
    setTimeout(() => {
      setSnackbar(prev => ({...prev, open: false}));
    }, 3000);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({...prev, open: false}));
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8, minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography>{t.loading}</Typography>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth={false} sx={{ width: '80%', margin: '0 auto', mt: 4, mb: 8 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <MuiLink 
            component={Link} 
            to="/" 
            color="inherit" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center'
            }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            {t.breadcrumbs.home}
          </MuiLink>
                      <MuiLink component={Link} to={`/bazars/${product.bazar.slug || 'chanchkoir-bazar'}`} color="inherit">
            {product.bazar.name}
          </MuiLink>
          <MuiLink 
            component={Link} 
            to="/medicine-bazar" 
            color="inherit"
            sx={{ 
              display: 'flex', 
              alignItems: 'center'
            }}
          >
            {t.breadcrumbs.medicineBazar}
          </MuiLink>
          <MuiLink 
            component={Link} 
            to={`/stores/${product.store.id}`} 
            color="inherit"
            sx={{ 
              display: 'flex', 
              alignItems: 'center'
            }}
          >
            {/* <PharmacyIcon sx={{ mr: 0.5 }} fontSize="small" /> */}
            {product.store.name}
          </MuiLink>
          <Typography color="text.primary">{product.name}</Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardMedia
                component="img"
                height="400"
                image={`https://picsum.photos/300/200?random=${13 + currentImageIndex}`}
                alt={product.name}
              />
              <Box sx={{ display: 'flex', p: 2 }}>
                {[0, 1, 2].map((index) => (
                  <Box 
                    key={index}
                    component="img"
                    src={`https://picsum.photos/300/200?random=${index + 13}`}
                    alt={`${product.name} view ${index + 1}`}
                    onClick={() => setCurrentImageIndex(index)}
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      objectFit: 'cover', 
                      borderRadius: 1,
                      mr: 1,
                      border: index === currentImageIndex ? '2px solid #5e35b1' : '1px solid #e0e0e0',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 1
                      }
                    }}
                  />
                ))}
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Chip 
                    icon={<HealingIcon />} 
                    label={product.category} 
                    color="primary" 
                    size="small" 
                    sx={{ mb: 2 }} 
                  />
                  {product.prescriptionRequired && (
                    <Chip 
                      label={t.labels.prescriptionRequired} 
                      color="warning" 
                      size="small" 
                      sx={{ ml: 1, mb: 2 }} 
                    />
                  )}
                  <Typography variant="h4" component="h1" gutterBottom>
                    {product.name}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    {product.manufacturer}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PharmacyIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" component={Link} to={`/stores/${product.store.id}`} sx={{ textDecoration: 'none', color: 'primary.main' }}>
                    {product.store.name}
                    {product.store.isVerified && (
                      <VerifiedIcon sx={{ ml: 0.5, fontSize: 16, color: '#2196f3' }} />
                    )}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Rating value={product.rating} precision={0.1} readOnly />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  ({product.reviewCount} {t.labels.reviewsSuffix})
                </Typography>
              </Box>

              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TimeIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="body2">
                    {t.labels.deliveryPrefix} {product.deliveryTime}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ShippingIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="body2">
                    {t.labels.minOrderPrefix} {product.minOrder} {product.unit}
                  </Typography>
                </Box>
              </Stack>
              
              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                {product.salePrice < product.price ? (
                  <>
                    <Typography variant="h4" color="primary" sx={{ mr: 2 }}>
                      ৳{product.salePrice}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      ৳{product.price}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="h4" color="primary">
                    ৳{product.price}
                  </Typography>
                )}
                <Typography variant="body1" sx={{ ml: 1 }}>
                  {t.labels.perUnitPrefix} {product.unit}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" paragraph>
                {product.description}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t.labels.expiry} <Chip 
                    label={product.expiryDate}
                    color="info" 
                    size="small" 
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t.labels.stock} <Chip 
                    label={product.stock > 0 ? t.labels.inStock : t.labels.outOfStock} 
                    color={product.stock > 0 ? 'success' : 'error'} 
                    size="small" 
                  />
                </Typography>
                {product.stock > 0 && (
                  <Typography variant="body2">
                    {product.stock} {product.unit} {t.labels.availableUnitsSuffix}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {t.labels.selectQuantity}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton onClick={decreaseQuantity} disabled={quantity <= 1}>
                        <RemoveIcon />
                      </IconButton>
                      <TextField
                        value={quantity}
                        onChange={handleQuantityChange}
                        type="number"
                        inputProps={{ 
                          min: 1, 
                          max: product.stock,
                          style: { textAlign: 'center' }
                        }}
                        sx={{ width: 80 }}
                      />
                      <IconButton onClick={increaseQuantity} disabled={quantity >= product.stock}>
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t.labels.unitLabel}</InputLabel>
                      <Select
                        value={selectedUnit}
                        onChange={handleUnitChange}
                        label={t.labels.unitLabel}
                      >
                        <MenuItem value="strip">{t.labels.unitStrip}</MenuItem>
                        <MenuItem value="box">{t.labels.unitBox}</MenuItem>
                        <MenuItem value="tablet">{t.labels.unitTablet}</MenuItem>
                        <MenuItem value="bottle">{t.labels.unitBottle}</MenuItem>
                        <MenuItem value="vial">{t.labels.unitVial}</MenuItem>
                        <MenuItem value="ampule">{t.labels.unitAmpule}</MenuItem>
                        <MenuItem value="tube">{t.labels.unitTube}</MenuItem>
                        <MenuItem value="sachet">{t.labels.unitSachet}</MenuItem>
                        <MenuItem value="inhaler">{t.labels.unitInhaler}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t.labels.perUnitNotePrefix} {unitConversions[selectedUnit].label}ে {getUnitInfo(selectedUnit)} {currentLanguage === 'en' ? 'contains' : 'রয়েছে'}
                </Typography>
                <Typography variant="body2" color="info.main" sx={{ mt: 1 }}>
                  {calculateTotalUnits()}
                </Typography>
                <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
                  {t.labels.totalAmountPrefix} ৳{((product.salePrice || product.price) * quantity * unitConversions[selectedUnit].price).toFixed(2)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', mt: 3 }}>
                <Button 
                  variant="contained"
                  size="large"
                  startIcon={<CartIcon />}
                  sx={{ mr: 2, flexGrow: 1 }}
                  onClick={handleAddToCart}
                >
                  {t.labels.addToCart}
                </Button>
                <IconButton color="primary" aria-label={t.labels.wishlistAria}>
                  <FavoriteIcon />
                </IconButton>
                <IconButton color="primary" aria-label={t.labels.shareAria}>
                  <ShareIcon />
                </IconButton>
              </Box>
              {product.prescriptionRequired && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  * {currentLanguage === 'en' ? 'Doctor’s prescription will be required to purchase this medicine' : 'এই ঔষধটি কেনার জন্য ডাক্তারের প্রেসক্রিপশন প্রয়োজন হবে'}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ mt: 4 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label={t.labels.descriptionTab} />
            <Tab label={t.labels.specsTab} />
            <Tab label={t.labels.reviewsTab} />
          </Tabs>
          <Box sx={{ p: 3 }}>
            {tabValue === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>{t.labels.productDescriptionTitle}</Typography>
                <Typography paragraph>{product.description}</Typography>
                <Typography paragraph>
                  {currentLanguage === 'en' ? 'All medicines are supplied from licensed pharmacies. Each medicine is stored at proper temperature and quality-checked regularly. We supply medicines only from trusted and approved pharmaceutical companies.' : 'আমাদের সকল ঔষধ লাইসেন্সপ্রাপ্ত ফার্মেসি থেকে সরবরাহ করা হয়। প্রতিটি ঔষধ সঠিক তাপমাত্রায় সংরক্ষণ করা হয় এবং নিয়মিত মান পরীক্ষা করা হয়। আমরা শুধুমাত্র বিশ্বস্ত ও অনুমোদিত ফার্মাসিউটিক্যাল কোম্পানির ঔষধ সরবরাহ করি।'}
                </Typography>
              </Box>
            )}
            {tabValue === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>{t.labels.productSpecsTitle}</Typography>
                <TableContainer>
                  <Table>
                    <TableBody>
                      {product.attributes.map((attribute, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row" sx={{ width: '30%', fontWeight: 'bold' }}>
                            {attribute.name}
                          </TableCell>
                          <TableCell>{attribute.value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            {tabValue === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>{t.labels.customerReviewsTitle}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Rating value={product.rating} precision={0.1} readOnly size="large" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {product.rating} এর মধ্যে 5
                  </Typography>
                </Box>
                <Typography paragraph>
                  {product.reviewCount} টি রিভিউ এর ভিত্তিতে
                </Typography>
                <Divider sx={{ my: 2 }} />
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="খুব ভালো কাজ করে এবং দ্রুত ডেলিভারি!"
                      secondary={
                        <>
                          <Rating value={5} size="small" readOnly />
                          <Typography variant="body2">সাইফুল ই. - ২ ঘন্টা আগে</Typography>
                        </>
                      } 
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="অরিজিনাল প্রোডাক্ট, ভালো প্যাকেজিং"
                      secondary={
                        <>
                          <Rating value={4} size="small" readOnly />
                          <Typography variant="body2">নাজমুল হ. - ১ দিন আগে</Typography>
                        </>
                      } 
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="সার্ভিস খুবই ভালো"
                      secondary={
                        <>
                          <Rating value={5} size="small" readOnly />
                          <Typography variant="body2">শাহানা ব. - ৩ দিন আগে</Typography>
                        </>
                      } 
                    />
                  </ListItem>
                </List>
              </Box>
            )}
          </Box>
        </Paper>

        <Box sx={{ mt: 6 }}>
            <Typography variant="h5" gutterBottom>
              {t.labels.relatedTitle}
          </Typography>
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={24}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            breakpoints={{
              640: { slidesPerView: 2 },
              768: { slidesPerView: 3 },
              1024: { slidesPerView: 4 },
            }}
          >
            {[1, 2, 3, 4].map((item) => (
              <SwiperSlide key={item}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <Link 
                    to={`/medicine-bazar/product/${item}`} 
                    style={{ textDecoration: 'none' }}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={`https://picsum.photos/300/200?random=${item + 20}`}
                      alt={`Related product ${item}`}
                    />
                  </Link>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Link 
                      to={`/medicine-bazar/product/${item}`} 
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Typography gutterBottom variant="h6" component="h3">
                        {['নাপা', 'এসিফিক্স', 'হিস্টাসিল', 'ওমেপ্রা'][item - 1]}
                      </Typography>
                    </Link>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {['বেক্সিমকো', 'স্কয়ার', 'ইনসেপ্টা', 'হেলথকেয়ার'][item - 1]}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      ৳{[120, 85, 95, 150][item - 1]}
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button 
                      size="small" 
                      variant="contained" 
                      fullWidth
                      startIcon={<CartIcon />}
                    >
                      {t.labels.addToCart}
                    </Button>
                  </Box>
                </Card>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Container>
      <Footer />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MedicineBazarProductDetail; 